import { Timestamp } from "firebase/firestore";

export const SyncStatus = {
  InProgress: "inprogress",
  Error: "error",
  Complete: "complete",
} as const;

export type SyncStatus = (typeof SyncStatus)[keyof typeof SyncStatus];

export type SyncDriver = {
  schemaVersion: 2;
  id: string;
  shopId: string;
  status: SyncStatus;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  lastSuccessfulSyncAt?: Timestamp | Date;
  lastSyncAttemptAt?: Timestamp | Date;
  /**
   * WEBV3-106: last time the bulk customer/vehicle/appointment freshness
   * checks actually ran AND succeeded for this shop. Absent => refresh due
   * (no migration needed). NEVER advanced on gated (skipped) runs -- the
   * next refresh's updatedSince window must span back to the last real
   * refresh or edits landing during skipped runs are permanently missed
   * (WEBV3-60 failure class).
   */
  lastEntityRefreshAt?: Timestamp | Date;
  /** WEBV3-106: last time the employees roster fetch ran and succeeded. */
  lastEmployeeRefreshAt?: Timestamp | Date;
  /**
   * WEBV3-272: last time the jobs-content freshness check (WEBV3-232)
   * actually ran AND succeeded for this shop. Independent of
   * lastEntityRefreshAt -- absent => refresh due (no migration needed),
   * NEVER advanced on gated or failed runs (same invariant as
   * lastEntityRefreshAt, see its comment above).
   */
  lastJobRefreshAt?: Timestamp | Date;
  fullSyncInProgress?: boolean;
  fullSyncRoLastPage?: number;
  backlogCardIds?: string[];
  claimedRequestedAt?: string;
};

export type SyncPriority = "initial" | "full" | "incremental";
export type SyncRequestStatus = "pending" | "processing" | "done" | "failed";

export type SyncRequest = {
  shopId: string;
  requestedAt: Timestamp | Date;
  priority: SyncPriority;
  status: SyncRequestStatus;
  requestedBy: "client" | "system";
  syncType: "incremental" | "full" | "fullReSync";
  /**
   * Durable continuation checkpoint for multi-page full syncs. Set when a
   * page completes and the request is re-marked "pending" for the next page;
   * absent (treated as 0) for fresh requests. createOrSkip's transaction.set
   * replaces the whole doc, so a stale value can never leak into a new request.
   */
  startingPage?: number;
  startedProcessingAt?: Timestamp | Date;
  completedAt?: Timestamp | Date;
  error?: string;
  /**
   * WEBV3-128 Rev 2: count of times resetStuckToPending has cycled this doc
   * from stuck "processing" back to "pending". Absent/undefined on legacy
   * docs is treated as 0. Once incrementing this exceeds STUCK_RESET_LIMIT
   * the doc is dead-lettered to "failed" instead of re-pended. Cleared by
   * markPendingContinuationIfCurrent on each completed page -- a completed
   * page is proof of progress, so the budget is "5 stuck episodes WITHOUT
   * progress," not a raw cycle count. Only ever incremented by the sweep's
   * resetStuckToPending.
   */
  stuckResets?: number;
};
